import { useCallback, useState } from 'react';

import type { CsvFlowState } from '@shared/dream-components';

import { useNonRevalidatingFetcher } from '~/hooks/useNonRevalidatingFetcher';
import type { CreateGroupSubscriptionBulkInvitationsActionData } from '~/routes/__actions/create_group_subscription_bulk_invitations';
import type { GroupSubscriptionBulkInvitationBatchLoaderData } from '~/routes/__loaders/group_subscription_bulk_invitation_batch';

const NON_CSV_ERROR_MESSAGE = "We couldn't read that file. Please try again by uploading a valid CSV file.";
const STILL_PROCESSING_MESSAGE =
  "This is taking longer than expected. We'll keep sending invitations in the background — check back in a few minutes.";
const LOST_ACCESS_MESSAGE = "We couldn't check on those invitations. Refresh the page and try again.";

// Mirrors the swarm client: a batch created a moment ago can 404 until it
// replicates to the follower, so a single miss is not proof it is gone.
const MAX_MISSING_BATCH_POLLS = 3;

/**
 * Multipart fetch can't report upload progress (no XHR), so we surface a fixed
 * non-zero value the modal renders as an indeterminate spinner state. 50 is
 * arbitrary — anything > 0 works; we just don't want an empty bar that reads
 * as "nothing happening." Same state covers the polling phase below — from the
 * user's perspective it's all one "working on it" spinner.
 */
const INDETERMINATE_UPLOAD_PROGRESS = 50;

const POLL_INTERVAL_MS = 2000;
// ~3 minutes, sized off staging timings: a normal batch completes in 26-32s, but
// one that arrives while another is still processing is deferred by the worker
// and lands at 62-70s. A budget of a minute or less times out on that case.
// Giving up stops the watching, not the work — Sidekiq keeps going either way.
const MAX_POLL_ATTEMPTS = 90;

/**
 * Real-backend bulk-CSV upload flow. Replaces `useFakeCsvUpload`. On overage,
 * Rails makes that decision synchronously and returns the valid_emails +
 * seats_needed envelope without creating anything; the consumer hands that off
 * to `AddSeatsAndInviteConfirmModal` for the seat-purchase flow.
 *
 * Otherwise Rails returns 202 with a batch id and processes seat creation +
 * invitation emails asynchronously (BEE-22119) — we poll the batch's status
 * until it lands on `completed`/`failed` before resolving.
 *
 * The dream-components `CsvFlowState['step'] = 'review'` is intentionally never
 * entered here — Rails has no preflight-then-send step for bulk. The modal's
 * review UI is now vestigial; we keep the state shape for type compatibility.
 */
export type BulkUploadResult =
  | { kind: 'success'; count: number; invalidRows: { email: string; reason: string }[] }
  | { kind: 'overage'; validEmails: string[]; seatsNeeded: number; costCents: number }
  // Distinct from 'error' — invites are still being sent in the background
  // (Sidekiq keeps working even though we stopped polling); nothing actually
  // failed, so this shouldn't read as a failure to the purchaser.
  | { kind: 'timeout'; message: string }
  | { kind: 'error'; error: string };

export type UseCsvUploadResult = {
  state: CsvFlowState;
  /** Kick off the real upload. Resolves with a discriminated result the host
   *  uses to drive toast/handoff transitions. */
  startUpload: (groupSubscriptionId: string, file: File) => Promise<BulkUploadResult>;
  clearUpload: () => void;
  setError: (message: string) => void;
};

function batchStatusUrl(groupSubscriptionId: string, batchId: string): string {
  const params = new URLSearchParams({ group_subscription_id: groupSubscriptionId, batch_id: batchId });
  return `/group_subscription_bulk_invitation_batch?${params.toString()}`;
}

type PollOutcome =
  | { kind: 'batch'; batch: NonNullable<GroupSubscriptionBulkInvitationBatchLoaderData['batch']> }
  | { kind: 'fatal'; message: string }
  | { kind: 'exhausted' };

/**
 * Polls the batch status loader until it lands on `completed`/`failed`, gives up after
 * `MAX_POLL_ATTEMPTS`, or stops early once the answer cannot change.
 *
 * That last case is the point: riding out a 403 for the full budget and then
 * reporting "we'll keep sending in the background" claims something we cannot
 * know. The longer the budget, the worse that lie gets.
 */
async function pollBatchStatus(groupSubscriptionId: string, batchId: string): Promise<PollOutcome> {
  const url = batchStatusUrl(groupSubscriptionId, batchId);
  let missingPolls = 0;

  for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt++) {
    await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));

    const response = await fetch(url);
    if (!response.ok) continue;

    const result = (await response.json()) as GroupSubscriptionBulkInvitationBatchLoaderData;
    if (result.status === 403) return { kind: 'fatal', message: LOST_ACCESS_MESSAGE };
    // Reads go to a follower, so a just-created batch can 404 until it replicates.
    // Tolerate a few before calling it gone.
    if (result.status === 404 && ++missingPolls >= MAX_MISSING_BATCH_POLLS) {
      return { kind: 'fatal', message: LOST_ACCESS_MESSAGE };
    }
    if (result.status !== 404) missingPolls = 0;

    if (result.batch && (result.batch.status === 'completed' || result.batch.status === 'failed')) {
      return { kind: 'batch', batch: result.batch };
    }
  }

  return { kind: 'exhausted' };
}

export function useCsvUpload(): UseCsvUploadResult {
  const [state, setState] = useState<CsvFlowState>({ step: 'upload' });
  const fetcher = useNonRevalidatingFetcher<() => Promise<CreateGroupSubscriptionBulkInvitationsActionData>>();

  const clearUpload = useCallback(() => setState({ step: 'upload' }), []);
  const setError = useCallback((message: string) => setState({ step: 'upload', error: message }), []);

  const startUpload = useCallback(
    async (groupSubscriptionId: string, file: File): Promise<BulkUploadResult> => {
      setState({ step: 'uploading', filename: file.name, progress: INDETERMINATE_UPLOAD_PROGRESS });

      const formData = new FormData();
      formData.append('group_subscription_id', groupSubscriptionId);
      formData.append('csv', file, file.name);

      const submitResult = await fetcher.submit(formData, {
        method: 'post',
        action: '/create_group_subscription_bulk_invitations',
        encType: 'multipart/form-data',
      });

      const body = submitResult.data;
      if (!submitResult.ok || !body) {
        const errorMessage = body?.error ?? NON_CSV_ERROR_MESSAGE;
        setState({ step: 'upload', error: errorMessage });
        return { kind: 'error', error: errorMessage };
      }
      if (body.error || !body.data) {
        const errorMessage = body.error ?? NON_CSV_ERROR_MESSAGE;
        setState({ step: 'upload', error: errorMessage });
        return { kind: 'error', error: errorMessage };
      }

      const data = body.data;
      // Overage branch — Rails did NOT create seats. The host hands off to the
      // seat-purchase confirm modal. Reset upload state since we're done with
      // the bulk modal flow.
      if ('overage' in data) {
        setState({ step: 'upload' });
        return {
          kind: 'overage',
          validEmails: data.valid_emails,
          seatsNeeded: data.overage.seats_needed,
          costCents: data.overage.cost_cents,
        };
      }

      // Async branch — Rails accepted the batch and is creating seats +
      // sending invites in the background. Keep showing the same indeterminate
      // spinner while we poll for completion.
      const outcome = await pollBatchStatus(groupSubscriptionId, data.batch_id);
      setState({ step: 'upload' });

      if (outcome.kind === 'exhausted') {
        return { kind: 'timeout', message: STILL_PROCESSING_MESSAGE };
      }
      if (outcome.kind === 'fatal') {
        setState({ step: 'upload', error: outcome.message });
        return { kind: 'error', error: outcome.message };
      }

      const { batch } = outcome;
      if (batch.status === 'failed') {
        return { kind: 'error', error: batch.error_message ?? 'Failed to process invitations' };
      }
      // Mid-flight capacity shortfall — the sync preflight passed, but a
      // concurrent upload/removal shifted remaining_seats before processing
      // actually ran. Same hand-off as the sync overage branch above.
      if (batch.overage) {
        return {
          kind: 'overage',
          validEmails: batch.valid_emails ?? [],
          seatsNeeded: batch.overage.seats_needed,
          costCents: batch.overage.cost_cents,
        };
      }
      return {
        kind: 'success',
        count: batch.seats_created_count,
        invalidRows: batch.invalid_rows,
      };
    },
    [fetcher],
  );

  return { state, startUpload, clearUpload, setError };
}
